/* eslint-disable camelcase */
import { useMutation } from '@tanstack/react-query'
import { getHeaders } from 'services/api/utils'
import { getDateWithClientTimezone } from 'utils/date'
import { findOutWhichEnv, getEnvPath } from 'utils/env'

const env = findOutWhichEnv()
const BASE_URL = `${getEnvPath(env)}/hsmservices/api`

/**
 * Fetches HSM (Human Support Messages) data based on provided filters.
 *
 * @param {Object} params - The parameters for filtering the data.
 * @param {Date} params.startDateTicks - The start date for filtering.
 * @param {Date} params.endDate - The end date for filtering.
 * @param {number} params.origin - The origin index of the messages.
 * @param {Array<string>} params.senders - The senders of the messages.
 * @param {Array<string>} params.categories - The categories of the messages.
 * @param {string} params.tenantId - The tenant ID for the messages.
 *
 * @returns {Promise<Object>} - A promise that resolves to the fetched data.
 */
async function generateHsmDataByFilters({ startDateTicks, endDateTicks, origin, senders, categories, tenantId }) {
  const changedEndDate = new Date(Number(endDateTicks) * 1000).setSeconds(59) + 999
  const payload = {
    startDate: getDateWithClientTimezone(new Date(startDateTicks * 1000)).utcString,
    endDate: getDateWithClientTimezone(new Date(changedEndDate)).utcString,
    origin,
    senders,
    categories,
    tenantId,
  }

  if (categories === null) delete payload.categories

  const response = await fetch(`${BASE_URL}/Hsm`, getHeaders('POST', payload)).then((r) => r.json())

  return response
}

function useHsmReport() {
  return useMutation({
    mutationKey: ['hsm-report-filtered-data'],
    mutationFn: async ({ startDateTicks, endDateTicks, origin, senders, categories, tenantId }) =>
      generateHsmDataByFilters({ startDateTicks, endDateTicks, origin, senders, categories, tenantId }),
    retry: 1,
  })
}

export default useHsmReport
